/**
 * Thông báo realtime (Socket.IO namespace `/thong-bao` của banhang-backend).
 * 1 kết nối dùng chung cả app — component đăng ký qua `useThongBaoRealtime`,
 * socket mở khi có người nghe đầu tiên và đóng khi người nghe cuối rời đi.
 *
 * URL: REACT_APP_WS_THONG_BAO_URL (vd https://api.example.com/thong-bao) nếu có;
 * không thì cùng origin với REACT_APP_API_SERVICE_URL, path `<pathname API>/socket.io`
 * (BE: /api/v1/socket.io) để đi chung proxy của API.
 */
import { io, type Socket } from 'socket.io-client'
import { apiUrl } from '@/config'
import { tokenStorage } from '@/services/tokenStorage'
import type { NhomThongBao } from '@/constants/thongBaoConstants'

/** Payload `thong-bao-moi` — cùng shape row danh-sach-thong-bao + cờ hienPopup. */
export interface ThongBaoRealtime {
  id: string
  title: string
  content: string
  loaiThongBao: string
  refId: string | null
  chiNhanhId: number | null
  nhom: NhomThongBao
  ngayTao: string
  watched: false
  laThongBaoChung: false
  /** false khi tôi đang tắt loại này trong cài đặt → chỉ cập nhật badge/danh sách. */
  hienPopup: boolean
}

export type ThongBaoRealtimeHandlers = {
  onMoi?: (tb: ThongBaoRealtime) => void
  /** Đã xem / xóa ở tab khác → tải lại số chưa đọc. */
  onCapNhat?: () => void
}

const SU_KIEN = { MOI: 'thong-bao-moi', CAP_NHAT: 'thong-bao-cap-nhat' } as const

const cauHinhKetNoi = (): { url: string; path: string } | null => {
  const explicit = process.env.REACT_APP_WS_THONG_BAO_URL?.trim()
  const api = apiUrl?.trim()
  try {
    const goc = new URL(api || '/', typeof window !== 'undefined' ? window.location.origin : undefined)
    const path = `${goc.pathname.replace(/\/+$/, '')}/socket.io`
    return { url: explicit || `${goc.origin}/thong-bao`, path }
  } catch {
    return null
  }
}

const nguoiNghe = new Set<ThongBaoRealtimeHandlers>()
let socket: Socket | null = null

const moKetNoi = () => {
  if (socket) return
  const cauHinh = cauHinhKetNoi()
  if (!cauHinh || !tokenStorage.getAccessToken()) return
  socket = io(cauHinh.url, {
    path: cauHinh.path,
    // Đọc token mỗi lần (re)connect — access token có thể đã được làm mới
    auth: (cb) => cb({ token: tokenStorage.getAccessToken() ?? '' }),
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
  })
  socket.on(SU_KIEN.MOI, (tb: ThongBaoRealtime) => {
    nguoiNghe.forEach((h) => h.onMoi?.(tb))
  })
  socket.on(SU_KIEN.CAP_NHAT, () => {
    nguoiNghe.forEach((h) => h.onCapNhat?.())
  })
  // BE ngắt khi token hết hạn/sai — thử lại sau, lúc đó token có thể đã được làm mới
  socket.on('disconnect', (reason) => {
    if (reason === 'io server disconnect') {
      setTimeout(() => {
        if (socket && !socket.connected && nguoiNghe.size) socket.connect()
      }, 15000)
    }
  })
  if (process.env.NODE_ENV !== 'production') {
    socket.on('connect_error', (err: Error) => console.warn('[thongBaoRealtime] connect_error:', err.message))
  }
}

const dongKetNoi = () => {
  socket?.removeAllListeners()
  socket?.close()
  socket = null
}

/** Đăng ký nghe thông báo realtime — trả về hàm hủy. */
export const dangKyThongBaoRealtime = (handlers: ThongBaoRealtimeHandlers): (() => void) => {
  nguoiNghe.add(handlers)
  moKetNoi()
  return () => {
    nguoiNghe.delete(handlers)
    if (!nguoiNghe.size) dongKetNoi()
  }
}
